import { useLayoutEffect, useRef, useState } from "react";
import { IoIosList } from "react-icons/io";

import { cn } from "@fronterahealth/frontera-ui-components/lib/utils";
import { Spinner } from "@fronterahealth/frontera-ui-components/ui/spinner";
import { Tooltip, TooltipContent, TooltipTrigger } from "@fronterahealth/frontera-ui-components/ui/tooltip";

import { CheckpointMark } from "@components/QaReview/components/CheckpointMark";
import { useIsPeerReviewEnabled } from "@components/QaReview/hooks/useIsPeerReviewEnabled";
import { useOpenReview } from "@components/QaReview/hooks/useOpenReview";
import { useQaScanState } from "@components/QaReview/hooks/useQaScanState";
import { useReviewData } from "@components/QaReview/hooks/useReviewData";
import { isDefaultReviewView, useReviewSession } from "@components/QaReview/hooks/useReviewSession";
import { useSidebarView } from "@providers/SidebarViewProvider/useSidebarView";

// Quality Review's line in the tooltip; no count until its flags are there to count.
const qaBreakdown = (isChecking: boolean, isQaFailed: boolean, count: number): string => {
  if (isChecking) return "Quality Review: checking";
  if (isQaFailed) return "Quality Review: didn't load";
  return `Quality Review: ${count}`;
};

const segmentClassName =
  "relative z-10 flex h-7 cursor-pointer items-center gap-2 rounded-md px-3 text-sm transition-colors duration-150 motion-reduce:transition-none";

/**
 * The Review header's two-way switch between the side panel's views: Outline and Checkpoint. Clicking only
 * switches the panel; the review bar has the view picker. Checkpoint carries the count of every
 * open item, what is left before signing, whichever view is picked.
 */
export const ReviewViewSwitch: React.FC = () => {
  const { openQaItems, openComments } = useReviewData();
  const { isChecking, isFailed: isQaFailed } = useQaScanState();
  const isPeerReviewEnabled = useIsPeerReviewEnabled();
  const { state } = useReviewSession();
  const { view, setView } = useSidebarView();
  const openReview = useOpenReview();
  const isReviewOpen = view === "review";

  // A rerun unloads Quality Review's flags until it finishes, which would drop the count and bring it back.
  // Hold the last finished scan's count while it checks, so the count changes once, when the scan lands.
  const [settledQaCount, setSettledQaCount] = useState<number | null>(null);
  if (!isChecking && !isQaFailed && settledQaCount !== openQaItems.length) setSettledQaCount(openQaItems.length);
  const qaCount = isChecking && settledQaCount !== null ? settledQaCount : openQaItems.length;
  const openCount = qaCount + openComments.length;
  const openBreakdown = [
    qaBreakdown(isChecking, isQaFailed, openQaItems.length),
    ...(isPeerReviewEnabled ? [`Team Review: ${openComments.length}`] : []),
  ];

  // The white pill slides to the picked segment.
  const outlineRef = useRef<HTMLButtonElement>(null);
  const checkpointRef = useRef<HTMLButtonElement>(null);
  const pillRef = useRef<HTMLSpanElement>(null);
  const pickedRef = isReviewOpen ? checkpointRef : outlineRef;
  useLayoutEffect(() => {
    const picked = pickedRef.current;
    const pill = pillRef.current;
    if (!picked || !pill) return;
    // Written straight to the element: layout, not React state.
    const measure = () => {
      pill.style.left = `${picked.offsetLeft}px`;
      pill.style.width = `${picked.offsetWidth}px`;
    };
    measure();
    // Re-measure when a segment's width changes: the count, the spinner, a late font.
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    [outlineRef.current, checkpointRef.current].forEach((segment) => segment && observer.observe(segment));
    return () => observer.disconnect();
  }, [pickedRef]);

  const showReview = () => {
    if (isReviewOpen) return;
    // Jump to the first item only until the reviewer has picked one, so switching back keeps their place.
    // The jump finds the report's first flagged field, which a narrowed view may not list.
    if (state.hasSelectedItem || !isDefaultReviewView(state.view)) setView("review");
    else openReview();
  };

  return (
    <fieldset
      aria-label="Side panel"
      className="relative m-0 flex min-w-0 items-center gap-0.5 rounded-lg border border-limestone-200 bg-limestone-50 p-0.5"
    >
      <span
        ref={pillRef}
        aria-hidden
        className="absolute top-0.5 bottom-0.5 rounded-md bg-white shadow-sm transition-[left,width] duration-150 ease-out motion-reduce:transition-none"
      />
      <button
        ref={outlineRef}
        type="button"
        aria-pressed={!isReviewOpen}
        data-dd-action-name="on this page"
        onClick={() => setView("toc")}
        className={cn(segmentClassName, isReviewOpen ? "text-text-secondary" : "font-medium text-text-primary")}
      >
        <IoIosList className="size-4" />
        Outline
      </button>
      <Tooltip>
        <TooltipTrigger
          render={
            <button
              ref={checkpointRef}
              type="button"
              aria-pressed={isReviewOpen}
              aria-label={`Checkpoint: ${openCount} open (${openBreakdown.join(", ")})${isChecking ? ", checking" : ""}`}
              data-dd-action-name="review filter menu"
              onClick={showReview}
              className={cn(segmentClassName, isReviewOpen ? "font-medium text-text-primary" : "text-text-secondary")}
            />
          }
        >
          <CheckpointMark className="size-4" />
          Checkpoint
          {isChecking && <Spinner aria-hidden role={undefined} className="size-3" />}
          {openCount > 0 && (
            // Open items only; nothing shows when there are none.
            <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-globemallow-100 px-1.5 text-xs font-semibold text-globemallow-700 tabular-nums">
              {openCount}
            </span>
          )}
        </TooltipTrigger>
        <TooltipContent>
          <div className="flex flex-col gap-1">
            <span>Checkpoint</span>
            {openBreakdown.map((line) => (
              <span key={line}>{line}</span>
            ))}
          </div>
        </TooltipContent>
      </Tooltip>
    </fieldset>
  );
};
